import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MdOutlineClose } from "react-icons/md";
import { useTranslation } from "next-i18next";
import useSWR from "swr";
import { GET_EW_MORE_INFO } from "@/services/extended-warranty";
import { sanitizeHtml } from "@/utils/sanitizeHtml";

/**
 * Extended-Warranty "more info" popup.
 *
 * The body is an HTML string returned by BE (warranty Conditions /
 * Depreciation / Deductible). The content is GLOBAL per store (not SKU/program
 * scoped), so the modal fetches it itself on open via GET_EW_MORE_INFO and
 * caches it with SWR (re-opening is instant). Pass an explicit `html` prop to
 * override the fetch (used by the isolation page). The injected HTML is styled
 * since BE markup won't carry Tailwind classes.
 *
 * Card: max-width 468px, radius 33px, content-driven height capped at 80vh
 * (the card itself scrolls, not the page). Overlay uses the house
 * `bg-lightblack opacity-50`. Closes on backdrop click + Escape; RTL-safe
 * (close button uses logical `end-`).
 *
 * Props:
 *   isOpen   — boolean
 *   onClose  — () => void
 *   html     — string (optional): render this directly instead of fetching
 *              (isolation/testing). Omit in real usage to fetch per-store.
 *   locale   — 'ar' | 'en'
 */
const EWMoreInfoModal = ({ isOpen, onClose, html, locale }) => {
  const { t } = useTranslation("extended_warranty");
  const isRTL = locale === "ar";
  const closeRef = useRef(null);

  // Portal target only exists client-side — guard SSR.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // No explicit `html` → fetch the global per-store content, but only once the
  // popup is opened. SWR caches it (keyed by locale) so re-opening is instant.
  const shouldFetch = isOpen && html === undefined;
  const { data: fetchedHtml, error: fetchError } = useSWR(
    shouldFetch ? ["ew-more-info", locale] : null,
    () => GET_EW_MORE_INFO(),
    { revalidateOnFocus: false, dedupingInterval: 600000 },
  );
  const content = html !== undefined ? html : fetchedHtml;
  const isFetching = shouldFetch && fetchedHtml === undefined && !fetchError;

  // While open: lock body scroll, close on Escape, focus the close button.
  useEffect(() => {
    if (!isOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape" && onClose) onClose();
    };
    document.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !mounted) return null;

  return createPortal(
    <div
      dir={isRTL ? "rtl" : "ltr"}
      role="dialog"
      aria-modal="true"
      aria-label={t("moreInfoAria", {
        defaultValue: "More information about this warranty",
      })}
      className="fixed inset-0 z-[9998] flex items-center justify-center p-4"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-lightblack opacity-50"
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Card */}
      <div
        className="relative z-10 w-full max-w-[468px] max-h-[80vh] overflow-y-auto bg-white rounded-[20px] md:rounded-[33px] px-4 pb-5 pt-12 md:px-6"
        style={{ boxShadow: "0px 1.656px 8.278px 0px rgba(0,0,0,0.10)" }}
      >
        {/* Close — top-end (right in LTR, left in RTL) */}
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label={t("close", { defaultValue: "Close" })}
          className="absolute top-4 end-4 flex items-center justify-center w-9 h-9 rounded-full bg-[#F2F2F2] text-[#888585] hover:bg-[#E8E8E8] transition-colors"
        >
          <MdOutlineClose className="text-2xl" />
        </button>

        {/* BE-returned HTML — loading / content / empty */}
        {isFetching ? (
          <div
            className="flex items-center justify-center py-10"
            aria-live="polite"
          >
            <span className="w-7 h-7 rounded-full border-2 border-[#00269A] border-t-transparent animate-spin" />
            <span className="sr-only">
              {t("loading", { defaultValue: "Loading..." })}
            </span>
          </div>
        ) : content ? (
          <div
            className="ew-moreinfo-content"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
          />
        ) : (
          <p className="py-6 text-center text-sm text-[#888585]">
            {t("moreInfoEmpty", {
              defaultValue: "No additional information available.",
            })}
          </p>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default EWMoreInfoModal;
